import React from "react";
import { useTranslation } from "react-i18next";
import { Star, LayoutGrid } from "lucide-react";
import type { Slot, PollInsights } from "../../lib/insights/types";
import { dayScores, type DayScore } from "../../lib/insights/dayScores";
import { formatDate } from "../voting/utils/dateUtils";
import type { SlotView } from "../../hooks/useSlotFilter";

interface SlotFilterChipsProps {
  slots: Slot[];
  insights: PollInsights;
  view: SlotView;
  onChange: (v: SlotView) => void;
  onOpenOverview?: () => void;
  /** Date du jour le mieux noté (préfixé ★ dans la liste des chips jour) */
  topDay?: string | null;
  /** Taille dynamique du Top — label devient "Top N" et la chip se masque
   *  automatiquement quand `topN >= slots.length` (équivalent à "Tous"). */
  topN?: number;
}

/**
 * Rangée de chips [★ Top 8] [Tous (N)] | [day chips…] | [Vue d'ensemble].
 * Persisté en URL via `useSlotFilter`. Cf. Results Final.html lignes 167-200.
 */
const SlotFilterChips: React.FC<SlotFilterChipsProps> = ({
  slots,
  insights,
  view,
  onChange,
  onOpenOverview,
  topDay = null,
  topN,
}) => {
  const { t } = useTranslation("polls");
  const days = dayScores(slots, insights);
  // dayScores ne connaît que primaryDate (clé de groupement) — le label
  // groupé (ex "Week-end du 12-13 sep") vit sur le Slot d'origine, retrouvé
  // ici pour l'afficher sur la chip au lieu de la date brute.
  const groupLabelByDate = new Map<string, string>();
  for (const s of slots) {
    if (s.groupLabel) groupLabelByDate.set(s.primaryDate, s.groupLabel);
  }
  const total = slots.length;
  const effectiveTopN = topN ?? Math.min(8, Math.ceil(total / 2));
  // Masquer la chip Top si elle équivaut à "Tous" (rien à filtrer).
  const showTopChip = total > 0 && effectiveTopN < total;
  const isTop = view.kind === "top";
  const isAll = view.kind === "all";
  const totalVoters = insights.participantsCount;

  const baseChip =
    "inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors cursor-pointer select-none";
  const inactive =
    "border-border bg-card text-muted-foreground hover:text-foreground";
  const active = "border-primary bg-primary/10 text-foreground";

  return (
    <div
      data-testid="slot-filter-chips"
      className="flex items-center gap-2 flex-wrap"
    >
      {showTopChip && (
        <button
          type="button"
          data-testid="chip-top"
          aria-pressed={isTop}
          onClick={() => onChange({ kind: "top" })}
          className={`${baseChip} ${isTop ? active : inactive}`}
        >
          <Star
            className={`h-3 w-3 ${isTop ? "text-vote-available-foreground fill-vote-available-foreground" : "text-muted-foreground"}`}
            aria-hidden
          />
          {t("slot_filter_top", { count: effectiveTopN })}
        </button>
      )}
      <button
        type="button"
        data-testid="chip-all"
        aria-pressed={isAll}
        onClick={() => onChange({ kind: "all" })}
        className={`${baseChip} ${isAll ? active : inactive}`}
      >
        {t("slot_filter_all", { count: total })}
      </button>

      <div className="h-5 w-px bg-border mx-1" aria-hidden />

      <div
        // Fade-mask sur les bords pour signaler le scroll horizontal quand il
        // y a plus de chips que de largeur. CSS mask-image gère ça sans JS.
        className="flex gap-1.5 overflow-x-auto pb-1 max-w-full [mask-image:linear-gradient(to_right,transparent,black_12px,black_calc(100%-12px),transparent)]"
        data-testid="chip-day-list"
      >
        {days.map((d) => {
          const isActive = view.kind === "day" && view.date === d.date;
          const isTopDay = topDay === d.date;
          const hot = totalVoters > 0 && d.bestYes >= totalVoters * 0.7;
          return (
            <button
              key={d.date}
              type="button"
              data-testid={`chip-day-${d.date}`}
              aria-pressed={isActive}
              onClick={() => onChange({ kind: "day", date: d.date })}
              className={`${baseChip} ${isActive ? active : inactive} min-w-[50px] sm:min-w-[80px] flex-col flex-shrink-0`}
            >
              <span className="text-[10px] font-semibold">
                {isTopDay && (
                  <span className="text-warning mr-0.5" aria-hidden>
                    ★
                  </span>
                )}
                {groupLabelByDate.get(d.date) ?? shortDay(d.date)}
              </span>
              <span
                className={`text-[11px] tabular-nums mt-0.5 ${hot ? "text-vote-available-foreground" : "text-muted-foreground"}`}
              >
                {d.bestYes}/{totalVoters}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex-1" />

      {onOpenOverview && (
        <button
          type="button"
          data-testid="chip-overview"
          onClick={onOpenOverview}
          className={`${baseChip} ${inactive} ml-auto`}
        >
          <LayoutGrid className="h-3 w-3" aria-hidden />
          {t("slot_filter_overview")}
        </button>
      )}
    </div>
  );
};

/** "lun. 25 mai" → "lun. 25 mai" — déjà compact (formatDate retourne ce format) */
function shortDay(date: string): string {
  return formatDate(date);
}

export default SlotFilterChips;
